import {
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { MongodbAtlasAiComponents } from "@p0security/integrations-mongodb-atlas-ai-shared/types";
import type { ItemConfigOf } from "@p0security/shared/install/types";
import { CommandDisplay, Typography } from "@p0security/ui";
import type React from "react";

import {
  type InstallContext,
  awsStoreClientSecret,
  awsTf,
  gcpStoreClientSecret,
  gcpTf,
} from "./iac.ts";

type SecretManagerConfig = ItemConfigOf<
  MongodbAtlasAiComponents["credential"]
>["secretManager"];

type SecretManagerType = SecretManagerConfig["type"];

type InstallCommands = ReturnType<
  NonNullable<
    NonNullable<
      FrontendInstaller<MongodbAtlasAiComponents>["components"]["credential"]
    >["instructions"]
  >
>;

/** What a secret manager contributes to the credential install step. Methods,
 * not properties, let an implementation narrow to its own member. */
type CredentialIac<T extends SecretManagerType = SecretManagerType> = {
  instructions(
    context: InstallContext,
    project: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): InstallCommands;
  /** The command that stores the client secret, if this secret manager has one */
  storeClientSecret(
    project: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): string | undefined;
};

const gcpSmIac: CredentialIac = {
  instructions: (context, project, secretManager) => ({
    commands: {
      iac: [
        {
          header:
            'Apply the following terraform to deploy P0\'s MongoDB Atlas connector, then click "Next".',
          command: gcpTf(context, project, secretManager),
        },
      ],
    },
  }),
  storeClientSecret: gcpStoreClientSecret,
} satisfies CredentialIac<"gcp-sm">;

const awsSmIac: CredentialIac = {
  instructions: (context, project, secretManager) => ({
    commands: {
      iac: [
        {
          header:
            'Apply the following terraform to deploy P0\'s MongoDB Atlas connector, then click "Next".',
          command: awsTf(context, project, secretManager),
        },
      ],
    },
  }),
  storeClientSecret: awsStoreClientSecret,
} satisfies CredentialIac<"aws-sm">;

// The Azure Key Vault option is hidden, and the backend refuses it, until the
// connector runs on Azure.
const azureKvIac: CredentialIac = {
  instructions: () => ({ commands: {} }),
  storeClientSecret: () => undefined,
} satisfies CredentialIac<"azure-kv">;

const credentialIac: Record<SecretManagerType, CredentialIac> = {
  "aws-sm": awsSmIac,
  "azure-kv": azureKvIac,
  "gcp-sm": gcpSmIac,
};

const { Paragraph } = Typography;

const AppSetup: React.FC<{
  id: string;
  secretManager: SecretManagerConfig;
}> = ({ id, secretManager }) => {
  const storeClientSecret = credentialIac[secretManager.type].storeClientSecret(
    id,
    secretManager
  );
  return (
    <div>
      <Paragraph type="secondary">
        In Atlas, open the project&apos;s Access Manager &gt; Applications &gt;
        Service Accounts, and create a service account for P0 with the Project
        Owner role. P0 uses it to create an MCP configuration for each grant,
        with the requested project roles.
      </Paragraph>
      <Paragraph type="secondary">
        If your organization requires an IP access list for the Atlas
        Administration API, add the connector&apos;s egress addresses to the
        service account&apos;s access list.
      </Paragraph>
      {storeClientSecret && (
        <>
          <Paragraph>Client secret</Paragraph>
          <Paragraph type="secondary">
            Set <code>ATLAS_CLIENT_SECRET</code> to the service account&apos;s
            client secret, and run this command to update the secret to it. The
            connector cannot manage MCP configurations until the secret holds
            the client secret.
          </Paragraph>
          <CommandDisplay commands={storeClientSecret} />
        </>
      )}
      <Paragraph>Client ID</Paragraph>
    </div>
  );
};

export const mongodbAtlasAiInstaller: FrontendInstaller<MongodbAtlasAiComponents> =
  {
    components: {
      credential: {
        instructions: (context, id, item) =>
          credentialIac[item.secretManager.type].instructions(
            context,
            id,
            item.secretManager
          ),
        items: {
          clientId: {
            labeler: (_, id, { secretManager, state }) =>
              state === "configure" ? (
                <AppSetup id={id} secretManager={secretManager} />
              ) : undefined,
          },
          secretManager: {
            options: {
              "aws-sm": {
                items: {
                  install: {
                    optionProvider: otherInstalledIntegration({
                      integration: "aws-sm",
                      component: "iam-write",
                    }),
                  },
                },
              },
              "gcp-sm": {
                items: {
                  install: {
                    optionProvider: otherInstalledIntegration({
                      integration: "gcp-sm",
                      component: "iam-write",
                    }),
                  },
                },
              },
            },
          },
        },
      },
    },
  };
